import { useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Button, DashboardModule, StatusBadge, Table, Text,
} from '@domain-group/suit-theme'

import useWebhook from '@hooks/useWebhook'
import useWebhooks from '@hooks/useWebhooks'

import type { WebhookModel } from '@models/types'

import WebhookActions from './WebhookActions'

const buildColumns = () => [
  {
    title: 'Title',
    dataKey: 'title',
    cellProps: {
      position: 'sticky',
      left: 0,
    },
  },
  {
    title: 'Version',
    cellProps: {
      width: 80,
      textAlign: 'center',
    },
    dataKey: 'webhook_version',
  },
  {
    title: 'Secret Key',
    dataKey: 'secret_key',
    render: (secretKey?: string) => (
      <Text variant="ellipsis">
        {secretKey}
      </Text>
    ),
  },
  {
    title: 'Webhook Url',
    dataKey: 'url',
    render: (url: string) => (
      <Text variant="ellipsis">
        {url}
      </Text>
    ),
  },
  {
    title: 'Events',
    cellProps: {
      textAlign: 'center',
      width: 150,
    },
    dataKey: 'enabled_events',
    // @ts-expect-error TS7006
    render: (_, webhook: WebhookModel) => {
      const { enable_all_events, enabled_events = {} } = webhook
      const eventCount = Object.keys(enabled_events).filter(trueEvent => enabled_events[trueEvent]).length || 0

      return enable_all_events ? 'All Events' : `${eventCount} Enabled`
    },
  },
  {
    title: 'Status',
    cellProps: {
      textAlign: 'center',
      width: 100,
    },
    dataKey: 'active',
    render: (active: boolean) => (
      <StatusBadge
        color={active ? 'green' : 'orange'}
        ghost
        text={active ? 'ACTIVE' : 'INACTIVE'}
      />
    ),
  },
  {
    title: 'Actions',
    dataKey: 'id',
    cellProps: {
      width: 80,
      textAlign: 'center',
    },
    render: (id: number) => <WebhookActions webhook={{ id }} />,
  },
]

const WebhooksTable = (props: { systemId: number }) => {
  const { systemId } = props

  const webhooksPayload = useWebhooks({
    filters: { owner_id: systemId, owner_type: 'System' },
    performHttpRequests: !!systemId,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredWebhooks,
    filteredWebhooksCount,
    hasFilteredWebhooks,
    loading,
  } = webhooksPayload

  const webhookPayload = useWebhook({ owner_id: systemId, owner_type: 'System' })
  const {
    callbacks: { createOrEditWebhook },
  } = webhookPayload

  const memoColumns = useMemo(() => buildColumns(), [filteredWebhooks.length])

  return (
    <DashboardModule contentBoxProps={{ flexDirection: 'column' }} loading={loading} title="Internal Webhooks">
      {!loading && hasFilteredWebhooks && (
        <Table
          boxProps={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}
          // @ts-expect-error TS2322
          columns={memoColumns}
          data={filteredWebhooks}
          scroll={{ x: 800 }}
          stickyHeader
        />
      )}

      {!loading && !hasFilteredWebhooks && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Webhooks Present
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please create a Webhook below
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      <DashboardModule.LoadMoreFooter
        boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
        callbacks={{ loadMore }}
        canLoadMore={canLoadMore}
        entityCount={filteredWebhooksCount}
        loading={loading}
      >
        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginLeft="auto"
          onClick={() => createOrEditWebhook()}
          // @ts-expect-error TS2322
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Webhook
        </Button>
      </DashboardModule.LoadMoreFooter>
    </DashboardModule>
  )
}

export default WebhooksTable
